import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge } from '../../../components/ui';
import { Wallet, RefreshCw, DollarSign, Calendar } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { diaOperativo } from '../../../utils/date';

// Tipos
interface MiSaldo {
  local_id: string;
  local_nombre: string;
  saldo_actual: string | null;
  es_propio?: boolean;
  credito?: {
    saldo_real?: string;
    saldo_post_sorteo?: string;
  } | null;
}

interface Movimiento {
  id: string;
  local_id: string;
  local_nombre: string;
  tipo: string;
  monto: string;
  referencia: string;
  fecha: string;
  saldo_resultante: string;
  detalle: {
    vendido: string;
    premios: string;
    comision: string;
  } | null;
  created_by: string;
  created_at: string;
}

type MovimientosResponse =
  | Movimiento[]
  | {
      items?: Movimiento[];
    };

const TIPO_LABELS: Record<string, string> = {
  LIQUIDACION_DIA: 'Liquidación',
  CIERRE_CONSOLIDADO: 'Cierre',
  ABONO: 'Abono',
  PAGO: 'Pago',
  PREMIO: 'Premio',
};

const TIPO_BADGE: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  LIQUIDACION_DIA: 'primary',
  CIERRE_CONSOLIDADO: 'neutral',
  ABONO: 'success',
  PAGO: 'warning',
  PREMIO: 'danger',
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

export const LocalSaldoPage: React.FC = () => {
  const [saldo, setSaldo] = useState<MiSaldo | null>(null);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [saldoData, movsData] = await Promise.all([
        fetchAPI<MiSaldo>('/saldos/mi-saldo'),
        fetchAPI<MovimientosResponse>('/saldos/movimientos?limit=100'),
      ]);

      const movimientosNormalizados = Array.isArray(movsData)
        ? movsData
        : Array.isArray(movsData?.items)
          ? movsData.items
          : [];

      setSaldo(saldoData);
      setMovimientos(movimientosNormalizados.filter(m => m.tipo === 'ABONO' || m.tipo === 'PAGO' || m.tipo === 'PREMIO'));
    } catch (e: any) {
      setError(e.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Saldo operativo real = saldo_actual (saldo_real). El saldo post-sorteo es solo
  // informativo (snapshot de sorteos cerrados) y NO debe usarse como saldo operativo:
  // de lo contrario los abonos no se ven reflejados en lo que el local debe pagar.
  const esPropio = saldo?.es_propio ?? false;
  const saldoMostrar = saldo?.credito?.saldo_real ?? saldo?.saldo_actual ?? '0';
  const saldoNum = Number(saldoMostrar);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Saldo</h1>
          <p className="text-gray-500">{saldo?.local_nombre || 'Estado financiero con la banca'}</p>
        </div>
        <button
          onClick={cargarDatos}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Saldo actual */}
      {!loading && saldo && (
        <Card>
          <div className="flex items-center justify-center py-6">
            <div className="text-center">
              <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl ${
                saldoNum > 0 ? 'bg-red-50 border-2 border-red-200' :
                saldoNum < 0 ? 'bg-green-50 border-2 border-green-200' :
                'bg-gray-50 border-2 border-gray-200'
              }`}>
                <Wallet size={32} className={
                  saldoNum > 0 ? 'text-red-500' :
                  saldoNum < 0 ? 'text-green-500' :
                  'text-gray-500'
                } />
                <div>
                  <p className={`text-4xl font-bold ${
                    saldoNum > 0 ? 'text-red-600' :
                    saldoNum < 0 ? 'text-green-600' :
                    'text-gray-600'
                  }`}>
                    {formatMonto(saldoMostrar)}
                  </p>
                  <p className={`text-sm mt-1 ${
                    saldoNum > 0 ? 'text-red-500' :
                    saldoNum < 0 ? 'text-green-500' :
                    'text-gray-500'
                  }`}>
                    {saldoNum > 0 ? 'Debes a la banca' :
                     saldoNum < 0 ? 'La banca te debe' :
                     'Saldo en cero'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{esPropio ? 'Saldo real' : 'Saldo post sorteo'}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8 text-gray-500">Cargando...</div>
      )}

      {/* Movimientos recientes (últimos 100) */}
      {!loading && (
        <Card padding="none">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-medium text-gray-700">Movimientos Recientes</h3>
          </div>
          {movimientos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Sin movimientos registrados</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {movimientos.map(m => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{m.fecha}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                          {TIPO_LABELS[m.tipo] || m.tipo}
                        </Badge>
                      </td>
                      <td className={`px-4 py-3 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Histórico con rango de fechas */}
      {!loading && <HistoricoAbonos />}
    </div>
  );
};

export default LocalSaldoPage;

// ─── Componente de histórico con rango de fechas ────────

const HistoricoAbonos: React.FC = () => {
  const hoy = diaOperativo();
  const hace30 = (() => {
    const d = new Date(hoy + 'T00:00:00');
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();

  const [fechaDesde, setFechaDesde] = useState(hace30);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [historico, setHistorico] = useState<Movimiento[]>([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [errorHist, setErrorHist] = useState('');

  const rangoError = (() => {
    if (!fechaDesde || !fechaHasta) return 'Seleccione ambas fechas';
    if (fechaDesde > fechaHasta) return 'Fecha inicio no puede ser mayor a fecha fin';
    const inicio = new Date(fechaDesde + 'T00:00:00');
    const fin = new Date(fechaHasta + 'T00:00:00');
    const diffDias = Math.round((fin.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDias > 90) return 'El rango máximo es de 90 días';
    return null;
  })();

  const cargarHistorico = useCallback(async () => {
    if (rangoError) return;
    setLoadingHist(true);
    setErrorHist('');
    try {
      const params = new URLSearchParams({
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        limit: '500',
      });
      const data = await fetchAPI<MovimientosResponse>(`/saldos/movimientos?${params}`);
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];
      setHistorico(items.filter(m => m.tipo === 'ABONO' || m.tipo === 'PAGO' || m.tipo === 'PREMIO'));
    } catch (e: any) {
      setErrorHist(e.message || 'Error al cargar histórico');
    } finally {
      setLoadingHist(false);
    }
  }, [fechaDesde, fechaHasta, rangoError]);

  useEffect(() => {
    cargarHistorico();
  }, [cargarHistorico]);

  const totalAbonos = historico
    .filter(m => m.tipo === 'ABONO')
    .reduce((sum, m) => sum + Math.abs(Number(m.monto)), 0);
  const totalPagos = historico
    .filter(m => m.tipo === 'PAGO')
    .reduce((sum, m) => sum + Math.abs(Number(m.monto)), 0);

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={cargarHistorico}
            disabled={loadingHist || !!rangoError}
            className="flex items-center gap-1 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Calendar size={14} />
            Buscar
          </button>
        </div>
        {rangoError && (
          <p className="mt-2 text-sm text-red-600">{rangoError}</p>
        )}
      </Card>

      {errorHist && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{errorHist}</div>
      )}

      {/* Resumen del periodo */}
      {!loadingHist && historico.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Abonos</p>
              <p className="text-xl font-bold text-green-600">₡{totalAbonos.toLocaleString('es-CR')}</p>
              <p className="text-xs text-gray-400">{historico.filter(m => m.tipo === 'ABONO').length} movimientos</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Pagos</p>
              <p className="text-xl font-bold text-red-600">₡{totalPagos.toLocaleString('es-CR')}</p>
              <p className="text-xs text-gray-400">{historico.filter(m => m.tipo === 'PAGO').length} movimientos</p>
            </div>
          </Card>
        </div>
      )}

      {/* Tabla histórica */}
      <Card padding="none">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-medium text-gray-700">
            Histórico de Abonos y Pagos ({historico.length})
          </h3>
        </div>
        {loadingHist ? (
          <div className="p-8 text-center text-gray-400">
            <RefreshCw size={24} className="mx-auto mb-2 animate-spin" />
            Cargando...
          </div>
        ) : historico.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Sin abonos ni pagos en este periodo</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700 hidden sm:table-cell">Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {historico.map(m => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">{m.fecha}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                        {TIPO_LABELS[m.tipo] || m.tipo}
                      </Badge>
                    </td>
                    <td className={`px-4 py-3 text-right font-mono font-medium ${m.tipo === 'ABONO' ? 'text-green-600' : 'text-red-600'}`}>
                      {(m.tipo === 'ABONO' || m.tipo === 'PREMIO') ? '-' : '+'}{formatMonto(m.monto)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                    <td className="px-4 py-3 text-right text-xs text-gray-400 hidden sm:table-cell">
                      ₡{formatMonto(m.saldo_resultante)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
